import * as Sentry from '@sentry/react';
import { toastr } from 'react-redux-toastr';
import { session_verdict, SESSION_REPLACE } from './cs_analysis_session';
import { cs_analysis_sign_out } from './cs_analysis_sign_out';

/**
 * Thin fetch wrapper for the cs-analysis service.
 *
 * Differences vs general_fetch:
 *   - Hits global.config.cs_analysis_path (not server_path).
 *   - Adds X-Requested-With: cs-analysis-csp on every request — cs-analysis's
 *     auth guard requires this for CSRF protection.
 *   - Does NOT unwrap a `data` envelope. cs-analysis returns raw JSON.
 *   - Default method is GET (most endpoints are reads).
 *   - Requests that are not JSON (an upload, a media read) go through
 *     cs_analysis_raw (end of this file): the same gate, the Response unparsed.
 *
 * Auth bootstrap. The legacy infurnia_main session cookie (`connect.sid`) is
 * host-only on preprodbackend.infurnia.com, so the browser refuses to send it
 * to cs-analysis subdomains. To unblock, on the first 401 we:
 *   1. Call `${server_path}/user/get_cs_comms_handoff` — the cookie *is* sent to
 *      that host even from another *.infurnia.com page (credentials:'include') —
 *      for a short-lived handoff token infurnia_main signs for the signed-in user.
 *   2. POST it to cs-analysis `/api/v1/auth/init` — which verifies it, looks the
 *      user up in the CS org's user list, and sets a `cs-analysis-session` cookie
 *      with Domain=.infurnia.com. From then on, that cookie travels everywhere.
 *   3. Retry the original request once.
 *
 * A refused sign-in is remembered (see sign_in_refusal below): the calls after
 * it fail at once until the page is shown or focused again.
 */

let bootstrap_in_flight = null;
let bootstrap_done = false;
// Cached after the first introspection. Used to impersonate the real
// logged-in user when targeting a local cs-analysis backend (see below).
let cached_user_details = null;

/**
 * True when cs-analysis is running at localhost. In that mode the SameSite
 * cookie can't travel from devdesign2.infurnia.com to http://localhost, so
 * cs-analysis runs a permissive local bypass. To still surface the *real*
 * logged-in user (so role-based filtering can do its job) the frontend
 * sends x-local-user-{id,email,roles} headers on every request when targeting
 * localhost; cs-analysis reads those instead of falling back to a synthetic
 * admin. Curl from terminal still gets admin (no headers).
 */
function is_local_cs_target() {
    const base = (global.config && global.config.cs_analysis_path) || '';
    return /^https?:\/\/localhost(?::\d+)?\/?/i.test(base);
}

/**
 * The page's infurnia-main user details, when the page already read them
 * (layouts/Admin.jsx's get_details): the session check below then reads them
 * from here instead of asking infurnia-main again.
 */
export function remember_user_details(user_details) {
    if (user_details && !cached_user_details) cached_user_details = user_details;
}

async function ensure_user_details() {
    if (cached_user_details) return cached_user_details;
    const server_base = (global.config && global.config.server_path) || '';
    if (!server_base) return null;
    const trimmed_server = server_base.replace(/\/+$/, '');
    try {
        const resp = await fetch(trimmed_server + '/user/get_details', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        });
        if (!resp.ok) return null;
        const j = await resp.json();
        cached_user_details = (j && j.user && j.user.user_details) || null;
        return cached_user_details;
    } catch (_) {
        return null;
    }
}

/**
 * A refused sign-in, remembered (CT-15747, 2026-11-04).
 *
 * Why: opening a client's org in the design app moves the person's Infurnia
 * session to that org for every tab. A CSP tab already open then gets a 401 on
 * each cs-analysis call, signed in again on every one of them (auth/init
 * answers 403 NOT_CS_ORG: the session is not on the CS org), and failed with
 * its panel's own error; only console.warn said why, and a visible comms
 * thread polls every 15 s.
 *
 * So a sign-in that is REFUSED (auth/init answers any 4xx, or infurnia-main's
 * handoff names no one: a 4xx, or no handoff in its answer) is kept here, and
 * until the page is shown again (`visibilitychange` to visible) or its window
 * focused, or the page reloads, the calls fail at once (the 401 callers get
 * today; nothing is sent) and nothing signs in again. Shown or focused again,
 * ONE new sign-in is allowed: the next call's 401 makes it (the person may have
 * moved back to the CS org, in this tab or another). A 5xx or no answer is not
 * a refusal: not kept, the next call signs in again as before.
 *
 * NOT_CS_ORG also tells the person, once per page (the app's toast, kept until
 * its close button: the tab may be in the background when it comes, and a
 * pointer passing over it does not take it away). The other refusals show
 * nothing new: the page's own errors, as before.
 */
export const NOT_CS_ORG_TOAST =
    'Your Infurnia session is now on another organisation. Reload this page and pick the Customer Success org to keep using the portal.';

let sign_in_refusal = null; // { step: 'handoff' | 'init', status, code }; null = a call may sign in
let not_cs_org_told = false;

function forget_sign_in_refusal() {
    sign_in_refusal = null;
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('focus', forget_sign_in_refusal);
}
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') forget_sign_in_refusal();
    });
}

/** A 4xx: the sign-in was refused (kept). A 5xx is not (the service, not the person). */
function is_refusal_status(status) {
    return status >= 400 && status < 500;
}

/** The api's refusal code ({ code, message }), or null. */
function refusal_code(text) {
    try {
        const j = JSON.parse(text);
        return j && typeof j.code === 'string' ? j.code : null;
    } catch (_) {
        return null;
    }
}

/** Keeps the refusal (and says NOT_CS_ORG, once a page); `err`, marked, for the bootstrap to throw. */
function keep_refusal(err, refusal) {
    sign_in_refusal = refusal;
    err.refusal = refusal;
    console.warn('cs-analysis sign-in refused: no sign-in again until this page is shown or focused again', refusal.step, refusal.status, refusal.code);
    if (refusal.step === 'init' && refusal.status === 403 && refusal.code === 'NOT_CS_ORG' && !not_cs_org_told) {
        not_cs_org_told = true;
        try {
            toastr.warning(NOT_CS_ORG_TOAST, { timeOut: 0, removeOnHover: false });
        } catch (_) {
            // The toast is a courtesy: the call's own error still surfaces.
        }
    }
    return err;
}

function refusal_word(refusal) {
    return refusal.code || refusal.step + ' ' + refusal.status;
}

async function bootstrap_session() {
    if (bootstrap_done) return true;
    if (sign_in_refusal) {
        const kept = new Error('cs_analysis_fetch bootstrap: the last sign-in was refused (' + refusal_word(sign_in_refusal) + '); not tried again until this page is shown or focused again');
        kept.refusal = sign_in_refusal;
        throw kept;
    }
    if (bootstrap_in_flight) return bootstrap_in_flight;

    bootstrap_in_flight = (async () => {
        const server_base = (global.config && global.config.server_path) || '';
        const cs_base = (global.config && global.config.cs_analysis_path) || '';
        if (!server_base || !cs_base) {
            throw new Error('cs_analysis_fetch bootstrap: missing server_path or cs_analysis_path');
        }

        const trimmed_server = server_base.replace(/\/+$/, '');
        const trimmed_cs = cs_base.replace(/\/+$/, '');

        // Step 1: ask infurnia_main for a signed handoff — who we are, signed by infurnia_main, good for two minutes
        // and for one sign-in. Cookie travels host-to-host, as for /user/get_details.
        const handoff_resp = await fetch(trimmed_server + '/user/get_cs_comms_handoff', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        });
        if (!handoff_resp.ok) {
            const err = new Error('cs_analysis_fetch bootstrap: /user/get_cs_comms_handoff ' + handoff_resp.status);
            if (is_refusal_status(handoff_resp.status)) throw keep_refusal(err, { step: 'handoff', status: handoff_resp.status, code: null });
            throw err;
        }
        const handoff_json = await handoff_resp.json();
        const handoff_token = handoff_json && handoff_json.data && handoff_json.data.handoff_token;
        if (!handoff_token) {
            // infurnia-main answered and named no one to sign in.
            throw keep_refusal(new Error('cs_analysis_fetch bootstrap: no handoff_token in /user/get_cs_comms_handoff response'), {
                step: 'handoff',
                status: handoff_resp.status,
                code: null,
            });
        }

        // Step 2: cs-analysis verifies the handoff, looks the person up in the CS org's user list, and mints the
        // cross-subdomain signed cookie with the roles that list gives.
        const init_resp = await fetch(trimmed_cs + '/api/v1/auth/init', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'cs-analysis-csp',
            },
            credentials: 'include',
            body: JSON.stringify({ handoff_token }),
        });
        if (!init_resp.ok) {
            const txt = await init_resp.text();
            const err = new Error('cs_analysis_fetch bootstrap: /auth/init ' + init_resp.status + ' ' + txt.slice(0, 200));
            if (is_refusal_status(init_resp.status)) throw keep_refusal(err, { step: 'init', status: init_resp.status, code: refusal_code(txt) });
            throw err;
        }

        bootstrap_done = true;
        return true;
    })();

    try {
        return await bootstrap_in_flight;
    } finally {
        bootstrap_in_flight = null;
    }
}

/**
 * The page-load session check (CT-15747; cs_analysis_session.js says why).
 * Once per page, before any other cs-analysis call: GET /api/v1/auth/me, and
 * the session's person and org compared with infurnia-main's get_details.
 *   - the signed-in person's: nothing more;
 *   - another person's or another org's, or a session while infurnia-main
 *     names nobody: sign in again now (bootstrap_session),
 *     so the cookie is replaced before any call is sent on it; if that fails,
 *     the cookie is cleared where the api can (its sign-out) and this page
 *     sends no cs-analysis call at all (a reload checks again);
 *   - no session: nothing; the first call's 401 signs in, as before;
 *   - the check could not tell (auth/me down or refusing otherwise): as before.
 * One GET per page load; nothing per call. Local targets have no cookie (the
 * x-local-user headers carry the person): not checked.
 */
let session_check = null;
let session_refused = false;

function check_session_once() {
    if (!session_check) {
        session_check = run_session_check().catch((err) => {
            console.warn('cs-analysis session check failed', err && err.message);
            return 'error';
        });
    }
    return session_check;
}

async function run_session_check() {
    if (is_local_cs_target()) return 'local';
    const cs_base = (global.config && global.config.cs_analysis_path) || '';
    if (!cs_base) return 'no_base';
    const details = await ensure_user_details();
    let me = null;
    try {
        const resp = await fetch(cs_base.replace(/\/+$/, '') + '/api/v1/auth/me', {
            method: 'GET',
            headers: { 'X-Requested-With': 'cs-analysis-csp' },
            credentials: 'include',
        });
        let user = null;
        if (resp.ok) {
            try {
                const j = await resp.json();
                user = (j && j.user) || null;
            } catch (_) {
                user = null;
            }
        }
        me = { status: resp.status, user };
    } catch (_) {
        me = null;
    }
    const verdict = session_verdict(me, details);
    if (verdict !== SESSION_REPLACE) return verdict;
    try {
        await bootstrap_session();
    } catch (boot_err) {
        // The browser holds a session that is not the signed-in person's and no
        // new one could be made: never send a call on it.
        session_refused = true;
        console.warn('cs-analysis: the session in this browser is not the signed-in person\'s; sign-in failed', boot_err && boot_err.message);
        await cs_analysis_sign_out();
    }
    return verdict;
}

/**
 * The gate every request to cs-analysis passes, cs_analysis_fetch's and cs_analysis_raw's alike, before anything is
 * sent (Copilot on #273, pass 2: the uploads went through a raw fetch, past it): the page-load session check; then a
 * browser session that is not the person's and could not be replaced, or a remembered refusal, fails at once with the
 * 401 the callers get (nothing sent, no sign-in).
 */
async function pass_gate(path) {
    await check_session_once();
    if (session_refused) {
        const refused = new Error(
            'cs-analysis 401: this browser holds a cs-analysis session that is not the signed-in person\'s and a new one could not be made; reload the page',
        );
        refused.status = 401;
        throw refused;
    }
    // A refused sign-in (sign_in_refusal): not sent (it could only be refused again), no sign-in; the 401 as today.
    if (sign_in_refusal) {
        const kept = new Error(
            `cs-analysis 401 at ${path}: not signed in to cs-analysis, and the sign-in was refused (${refusal_word(sign_in_refusal)}); ` +
                'tried again when this page is shown or focused again, or reloaded',
        );
        kept.status = 401;
        kept.body = { statusCode: 401, code: 'SIGN_IN_REFUSED', refusal: refusal_word(sign_in_refusal) };
        throw kept;
    }
}

function request_url(path, query) {
    const base = (global.config && global.config.cs_analysis_path) || '';
    const trimmedBase = base.replace(/\/+$/, '');
    const trimmedPath = path.startsWith('/') ? path : '/' + path;
    let url = trimmedBase + trimmedPath;

    if (query && typeof query === 'object') {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
        });
        const qs = params.toString();
        if (qs) url += '?' + qs;
    }
    return url;
}

/**
 * The headers every request carries: X-Requested-With, the caller's, and on a local target the x-local-user ones.
 * `json`: a Content-Type: application/json first (cs_analysis_fetch's body is JSON text); cs_analysis_raw sends none.
 */
async function request_headers(headers, json) {
    const reqHeaders = {
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        'X-Requested-With': 'cs-analysis-csp',
        ...(headers || {}),
    };

    // Local cs-analysis: thread the real logged-in user through so the
    // backend's local bypass can build the correct session user (impersonation
    // by header). One extra introspection call on the very first request,
    // then cached for the session.
    if (is_local_cs_target()) {
        const u = await ensure_user_details();
        if (u && u.id && u.email) {
            reqHeaders['x-local-user-id'] = String(u.id);
            reqHeaders['x-local-user-email'] = String(u.email);
            const roles = (u.role && Array.isArray(u.role.store)) ? u.role.store : [];
            reqHeaders['x-local-user-roles'] = roles.join(',');
            if (u.first_name) reqHeaders['x-local-user-first-name'] = String(u.first_name);
            if (u.last_name) reqHeaders['x-local-user-last-name'] = String(u.last_name);
            if (u.store_id) reqHeaders['x-local-user-store-id'] = String(u.store_id);
        }
    }
    return reqHeaders;
}

/**
 * A 401 on a request: signs in again (once per request: `_retried`) and says whether to send it again. A 401 after an
 * earlier bootstrap means that session ended (expired or refused); concurrent 401s still share bootstrap_in_flight. A
 * failed sign-in says no: the caller surfaces the original 401.
 */
async function signed_in_again(resp, path, _retried) {
    if (resp.status !== 401 || _retried || path === '/api/v1/auth/init') return false;
    try {
        bootstrap_done = false;
        await bootstrap_session();
        return true;
    } catch (boot_err) {
        console.warn('cs-analysis auth bootstrap failed', boot_err && boot_err.message);
        return false;
    }
}

const cs_analysis_fetch = async ({ path, method = 'GET', body, query, headers }, _retried = false) => {
    if (!path) throw new Error('cs_analysis_fetch: path required');

    await pass_gate(path);

    const url = request_url(path, query);
    const reqHeaders = await request_headers(headers, true);

    const options = {
        method,
        headers: reqHeaders,
        credentials: 'include',
    };
    if (body !== undefined && method !== 'GET' && method !== 'HEAD') {
        options.body = JSON.stringify(body);
    }

    let resp;
    try {
        resp = await fetch(url, options);
    } catch (err) {
        Sentry.captureException(err, { fingerprint: ['cs_analysis_fetch_network'] });
        throw new Error('cs-analysis network error');
    }

    if (await signed_in_again(resp, path, _retried)) {
        return cs_analysis_fetch({ path, method, body, query, headers }, true);
    }

    let payload = null;
    const text = await resp.text();
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch (_) {
            payload = text;
        }
    }

    if (!resp.ok) {
        const status = resp.status;
        if (status === 401 || status === 403) {
            console.warn('cs-analysis auth failed', status, payload);
        }
        const err = new Error(
            `cs-analysis ${status} ${resp.statusText} at ${path}: ` +
                (typeof payload === 'string' ? payload : JSON.stringify(payload)).slice(0, 300),
        );
        err.status = status;
        err.body = payload;
        throw err;
    }

    return payload;
};

/**
 * The one entry for the cs-analysis requests that are not JSON: a multipart upload (`form`, a FormData) or a media
 * read (a GET whose answer is bytes). The same gate as cs_analysis_fetch, in its order (pass_gate: the page-load
 * session check, a refused session, a remembered refusal), the same headers (request_headers) but never a
 * Content-Type, so the browser writes the FormData's own multipart one with its boundary (one a caller passes is
 * dropped for a FormData), the FormData sent untouched, and the same 401 -> sign in again -> one retry.
 *
 * Resolves with the fetch Response, a non-2xx one included, for the caller to read as before (the uploads'
 * upload_error, the media read's blob()); rejects with the gate's 401 errors (cs_analysis_fetch's shape: status, and
 * body.code SIGN_IN_REFUSED for a remembered refusal) or fetch's own error when nothing answered.
 *
 * Every request to cs-analysis goes through this or cs_analysis_fetch (utils/cs_analysis_requests_guard.test.js);
 * cs_analysis_sign_out alone sends its own, on purpose (cs_analysis_sign_out.js says why).
 */
export async function cs_analysis_raw({ path, method = 'GET', form, query, headers }, _retried = false) {
    if (!path) throw new Error('cs_analysis_raw: path required');

    await pass_gate(path);

    const url = request_url(path, query);
    const reqHeaders = await request_headers(headers, false);
    if (typeof FormData !== 'undefined' && form instanceof FormData) {
        Object.keys(reqHeaders).forEach((k) => {
            if (k.toLowerCase() === 'content-type') delete reqHeaders[k];
        });
    }

    const options = {
        method,
        headers: reqHeaders,
        credentials: 'include',
    };
    if (form !== undefined && method !== 'GET' && method !== 'HEAD') {
        options.body = form;
    }

    const resp = await fetch(url, options);

    if (await signed_in_again(resp, path, _retried)) {
        return cs_analysis_raw({ path, method, form, query, headers }, true);
    }
    return resp;
}

window.cs_analysis_fetch = cs_analysis_fetch;
export default cs_analysis_fetch;
